import { FormControl, FormControlLabel, FormHelperText } from '@mui/material';

import type {
  FormControlLabelProps,
  FormControlProps,
  FormHelperTextProps,
  FormLabelProps,
} from '@mui/material';
import type { FormControlTypeMap } from '@mui/material/FormControl/FormControl';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { DOMAttributes, FC } from 'react';
import type React from 'react';

type RemoveDupl<T> = Omit<
  T,
  'children' | 'sx' | 'classes' | 'className' | 'style' | keyof DOMAttributes<'div'>
>;

export interface FormFieldControlProps
  extends
    RemoveDupl<FormControlProps>,
    Omit<RemoveDupl<FormControlLabelProps>, 'color' | 'ref' | 'control'>,
    Omit<RemoveDupl<FormHelperTextProps>, 'color' | 'margin'>
{
  component?: React.ElementType;
  className?: FormControlProps['className'];
  style?: FormControlProps['style'];
  classes?: FormControlProps['classes'];
  sx?: FormControlProps['sx'];
  onChange?: FormControlLabelProps['onChange'];
  FormLabel?: React.ReactElement<FormLabelProps>;
  helperText?: React.ReactNode;
  children: FormControlLabelProps['control'];
}

export const FormFieldControl: FC<FormFieldControlProps> = ({
  /* FormControlProps */
  error,
  required,
  classes,
  variant,
  color,
  focused,
  disabled,
  fullWidth,
  hiddenLabel,
  margin,
  size,
  sx,
  style,
  className,
  component,

  /* FormControlLabelProps */
  label,
  name,
  ref,
  checked,
  disableTypography,
  value,
  inputRef,
  labelPlacement,
  onChange,
  slotProps,
  componentsProps,
  children,

  /* FormHelperTextProps */
  filled,
  helperText,

  /* FormLabel */
  FormLabel,
}) => (
  <FormControl
    error={error}
    required={required}
    classes={classes}
    variant={variant}
    color={color}
    focused={focused}
    disabled={disabled}
    sx={sx}
    fullWidth={fullWidth}
    hiddenLabel={hiddenLabel}
    margin={margin}
    size={size}
    style={style}
    className={className}
    component={component}
  >
    {FormLabel}
    <FormControlLabel
      required={required}
      disabled={disabled}
      label={label}
      name={name}
      ref={ref}
      checked={checked}
      disableTypography={disableTypography}
      value={value}
      inputRef={inputRef}
      labelPlacement={labelPlacement}
      onChange={onChange}
      slotProps={slotProps}
      componentsProps={componentsProps}
      control={children}
    />
    <FormHelperText
      required={required}
      disabled={disabled}
      focused={focused}
      margin={margin === 'dense' ? margin : void 0}
      error={error}
      variant={variant}
      filled={filled}
    >
      {helperText}
    </FormHelperText>
  </FormControl>
);
